import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { FileText, Trash2 } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { GAME_TYPES } from '../../utils/constants';
import ScorecardDetailView from '../../components/scorecards/ScorecardDetailView';

const MyScorecards = () => {
  const { currentUser, setActiveGame, setView, showConfirm } = useContext(AppContext);
  const [activeScorecards, setActiveScorecards] = useState([]);
  const [completedGames, setCompletedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [activeResult, completedResult] = await Promise.allSettled([
        firestoreService.getUserActiveScorecards(currentUser.id),
        firestoreService.getUserCompletedGames(currentUser.id),
      ]);
      if (activeResult.status === 'fulfilled') setActiveScorecards(activeResult.value);
      else console.error('Error fetching active scorecards:', activeResult.reason);
      if (completedResult.status === 'fulfilled') setCompletedGames(completedResult.value);
      else console.error('Error fetching completed games:', completedResult.reason);
      setLoading(false);
    };
    fetchData();
  }, [currentUser.id]);

  const handleResume = (game) => {
    setActiveGame({ ...game });
    setView('live-score');
  };

  const handleDelete = async (game) => {
    if (!await showConfirm('Delete this scorecard? This cannot be undone.', 'Delete Scorecard', 'Delete')) return;
    try {
      await firestoreService.deleteGame(game.id);
      setActiveScorecards(prev => prev.filter(g => g.id !== game.id));
      setCompletedGames(prev => prev.filter(g => g.id !== game.id));
      setSelectedGame(null);
    } catch (err) {
      console.error('Error deleting scorecard:', err);
    }
  };

  const isWithin24Hours = (isoDate) => {
    if (!isoDate) return false;
    return (Date.now() - new Date(isoDate).getTime()) < 24 * 60 * 60 * 1000;
  };

  const hoursRemaining = (isoDate) => {
    if (!isoDate) return 0;
    const ms = (new Date(isoDate).getTime() + 24 * 60 * 60 * 1000) - Date.now();
    return Math.max(1, Math.ceil(ms / (60 * 60 * 1000)));
  };

  if (selectedGame) {
    return <ScorecardDetailView game={selectedGame} onBack={() => setSelectedGame(null)} onDelete={handleDelete} />;
  }

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
        <FileText className="w-6 h-6 text-[#16a34a]" />
        <span>My Scorecards</span>
      </h2>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]"></div>
        </div>
      ) : (
        <>
          {/* In-progress */}
          {activeScorecards.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{activeScorecards.length} In Progress</h3>
              <div className="space-y-3">
                {activeScorecards.map(game => {
                  const gameTypeInfo = GAME_TYPES.find(g => g.id === game.gameType);
                  const holes = game.holes || {};
                  const played = Object.values(holes).filter(h => Object.keys(h.scores || {}).length > 0).length;
                  return (
                    <div key={game.id} className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-bold">{game.courseName}</p>
                          <p className="text-gray-400 text-sm">
                            {gameTypeInfo?.emoji} {gameTypeInfo?.name || game.gameType} · Hole {game.currentHole || 1}/{game.totalHoles || 18}
                            {played > 0 && ` · ${played} holes scored`}
                          </p>
                          <p className="text-gray-400 text-xs mt-1">{formatDate(game.startedAt)}</p>
                        </div>
                        <span className="text-xs bg-yellow-600 text-white px-2 py-0.5 rounded-full">Paused</span>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={() => handleResume(game)} className="flex-1 py-2 bg-[#16a34a] text-pure-white rounded-lg font-bold text-sm">
                          Resume
                        </button>
                        <button onClick={() => handleDelete(game)} className="px-4 py-2 bg-gray-800 text-red-400 rounded-lg text-sm hover:bg-gray-700">
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Completed */}
          {completedGames.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{completedGames.length} Completed</h3>
              <div className="space-y-3">
                {completedGames.map(game => {
                  const gameTypeInfo = GAME_TYPES.find(g => g.id === game.gameType);
                  const holes = game.holes || {};
                  const userId = currentUser.id;
                  const totalScore = Object.values(holes).reduce((s, h) => s + (h.scores?.[userId] || 0), 0);
                  const totalPar = Object.values(holes).reduce((s, h) => s + (h.par || 4), 0);
                  const diff = totalScore - totalPar;
                  const isEvent = !!game.eventId;
                  const canDelete = isWithin24Hours(game.completedAt);
                  return (
                    <div key={game.id} className="bg-gray-900 rounded-lg border border-gray-800 hover:border-gray-600 transition-colors">
                      <button
                        onClick={() => setSelectedGame(game)}
                        className="w-full p-4 text-left"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-bold truncate">{game.courseName}</p>
                            <p className="text-gray-400 text-sm">
                              {gameTypeInfo?.emoji} {gameTypeInfo?.name || game.gameType || 'Stroke'} · {game.totalHoles || Object.keys(holes).length} holes
                              {isEvent && <span className="ml-2 text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">Event</span>}
                            </p>
                            <p className="text-gray-400 text-xs mt-1">{formatDate(game.completedAt)}</p>
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            <p className="text-white font-bold text-lg">{totalScore || '—'}</p>
                            <p className={`text-sm font-bold ${diff <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {totalScore > 0 ? (diff >= 0 ? `+${diff}` : `${diff}`) : ''}
                            </p>
                          </div>
                        </div>
                      </button>
                      {canDelete && (
                        <div className="px-4 pb-3 pt-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(game); }}
                            className="text-xs text-red-400 hover:text-red-300 flex items-center space-x-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Delete this scorecard in the next {hoursRemaining(game.completedAt)} hour{hoursRemaining(game.completedAt) !== 1 ? 's' : ''}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : activeScorecards.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No scorecards yet</p>
              <button onClick={() => setView('new-scorecard')} className="mt-4 px-6 py-2 bg-[#16a34a] text-pure-white rounded-lg font-bold">
                Start a Round
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== NEW SCORECARD ====================

export default MyScorecards;
